import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";

import type { SearchResult } from "~/shared/components/fields/address-postal-code-search.tsx";
import { AddressPostalCodeSearch } from "~/shared/components/fields/address-postal-code-search.tsx";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";

import type { EditDescriptor } from "../../hooks/use-address-editing.ts";

// Fields the postal-code selection writes back onto the address row.
export type CityStateZipFields = {
	city: string;
	stateProvince: string;
	postalCode: string;
	country: string;
};

type CityStateZipCellProps = GridCustomCellProps & {
	edit: EditDescriptor;
	onApply: (
		dataItem: AddressBookItemSummary,
		fields: CityStateZipFields,
	) => void;
};

type EditableItem = AddressBookItemSummary & { cityStateZip?: string };

/**
 * The "City, State, Zip" column. Renders read-only text normally; while its row
 * is being edited it renders a postal-code search restricted to postal codes
 * only (no saved-address suggestions), auto-filling city/state/zip/country on
 * selection. The row's other search inputs are left untouched.
 *
 * NOTE: this input keeping focus depends on the grid's `columnConfig` staying
 * referentially stable while a row is being edited. If the column config
 * regenerates (e.g. a new/unstable handler in its deps), Kendo remounts every
 * cell and this input loses focus. See `address-book-modal.tsx`.
 */
export const CityStateZipCell = ({
	edit,
	onApply,
	...props
}: CityStateZipCellProps) => {
	const { tdProps } = props;
	const item = props.dataItem as EditableItem;
	const isInEdit = edit[item.addressBookItemId] === true;

	if (!isInEdit) {
		return <td {...tdProps}>{item.cityStateZip ?? ""}</td>;
	}

	const handleSelectionChange = (result: SearchResult | null) => {
		const zip = result?.type === "postalCode" ? result.data : null;
		if (!zip) {
			return;
		}
		onApply(item, {
			city: zip.city.name,
			stateProvince: zip.city.state.abbreviation,
			postalCode: zip.postalCode,
			country: zip.city.state.countryCode,
		});
	};

	return (
		<td {...tdProps}>
			<AddressPostalCodeSearch
				id={`city-state-zip-${item.addressBookItemId}`}
				onSelectionChange={handleSelectionChange}
				onlySearchPostalCodes={true}
				placeholder={"Postal Code"}
				showFullAddressOnSelect={false}
				showIcons={false}
				value={item.postalCode ?? ""}
			/>
		</td>
	);
};
